import { Fragment } from 'react';
import {
  Box, Typography, Paper, TextField, Button, MenuItem, Select,
  FormControl, InputLabel, Radio, RadioGroup, FormControlLabel, FormLabel,
  IconButton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Collapse, Chip, Alert, Dialog, DialogTitle, DialogContent, DialogActions,
  ToggleButton, ToggleButtonGroup, Autocomplete,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import NotesIcon from '@mui/icons-material/Notes';
import PreviewIcon from '@mui/icons-material/Preview';
import EmailIcon from '@mui/icons-material/Email';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import dayjs from 'dayjs';
import WarrantyTicketOrderTypeSelector from './WarrantyTicketOrderTypeSelector';
import WarrantyTicketPhotoPicker from './WarrantyTicketPhotoPicker';
import { PRIORITIES, WORK_STATUSES } from '../hooks/useWarrantyTicketForm';

const PARTS_STATUSES = ['Parts needed', 'Parts ordered', 'Parts received'];

/** Part vs Equipment switch that drives the failed-part autofill.
 *  Color-coded so techs notice which mode a row is in: blue = part,
 *  orange = whole equipment. */
export const partDescPlaceholder = (part) =>
  (part.itemType === 'equipment' ? 'Equipment model *' : 'Part description *');

export const partDescSx = (part) =>
  (part.itemType === 'equipment'
    ? { '& input': { color: 'warning.dark', fontWeight: 600 } }
    : { '& input': { color: 'info.dark' } });

/** Header of the description column: a colored badge matching the rows' type
 *  (neutral text when the rows are a mix of parts and equipment). Rendered as
 *  an inline badge because the global stylesheet forces a grey background on
 *  table header cells. */
export const PartsHeaderLabel = ({ rows }) => {
  const types = new Set(rows.map((p) => p.itemType || 'part'));
  if (types.size !== 1) return <strong>Part / Equipment</strong>;
  const equipment = types.has('equipment');
  return (
    <Box component="span" sx={{
      display: 'inline-block', px: 1.25, py: 0.4, borderRadius: 0.75,
      bgcolor: equipment ? 'warning.main' : 'info.main',
      color: 'common.white', fontWeight: 700, fontSize: 12, lineHeight: 1.2,
    }}>
      {equipment ? 'Equipment' : 'Part'}
    </Box>
  );
};

/** Colored "Part #1" / "Equipment #1" badge for the mobile part cards. */
export const PartCardBadge = ({ part, index }) => (
  <Typography variant="caption" sx={{
    fontWeight: 700, color: 'common.white', px: 0.75, py: 0.25, borderRadius: 0.5,
    bgcolor: (part.itemType === 'equipment') ? 'warning.main' : 'info.main',
  }}>
    {(part.itemType === 'equipment') ? 'Equipment' : 'Part'} #{index + 1}
  </Typography>
);

export const PartTypeToggle = ({ value, onChange, sx }) => (
  <ToggleButtonGroup exclusive size="small" value={value || 'part'}
    onChange={(e, v) => { if (v) onChange(v); }} sx={sx}>
    <ToggleButton value="part" sx={{
      px: 1, py: 0.1, fontSize: 11, textTransform: 'none',
      '&.Mui-selected': { bgcolor: 'info.main', color: 'common.white', fontWeight: 700, '&:hover': { bgcolor: 'info.dark' } },
    }}>Part</ToggleButton>
    <ToggleButton value="equipment" sx={{
      px: 1, py: 0.1, fontSize: 11, textTransform: 'none',
      '&.Mui-selected': { bgcolor: 'warning.main', color: 'common.white', fontWeight: 700, '&:hover': { bgcolor: 'warning.dark' } },
    }}>Equipment</ToggleButton>
  </ToggleButtonGroup>
);

/**
 * Dropdown over the property's Installed Equipment card (property.equipment).
 * Picking an item autofills the defective model/serial (and install year) so
 * techs don't retype what the CRM already knows. Manual typing stays available
 * for equipment installed before the new system.
 */
export const InstalledEquipmentPicker = ({ equipment, onPick, size = 'small', sx }) => {
  const options = (equipment || []).filter((item) => item && (item.model || item.serialNumber));
  if (!options.length) return null;
  return (
    <Autocomplete
      options={options}
      getOptionLabel={(item) => `${item.model || 'Unknown model'} — SN ${item.serialNumber || '—'}`}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      renderOption={(props, item) => (
        <li {...props} key={item.id || `${item.model}-${item.serialNumber}`}>
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {item.model || 'Unknown model'}
              {item.replacedByWarranty && (
                <Typography component="span" variant="caption" color="warning.main" sx={{ ml: 1, fontWeight: 600 }}>
                  replaced by warranty
                </Typography>
              )}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              SN {item.serialNumber || '—'}
              {item.installDate ? ` · installed ${item.installDate}` : ''}
              {item.poNumber ? ` · PO ${item.poNumber}` : ''}
            </Typography>
          </Box>
        </li>
      )}
      onChange={(e, item) => { if (item) onPick(item); }}
      renderInput={(params) => (
        <TextField {...params} label="Select from Installed Equipment" size={size}
          placeholder="Search by model or serial…" />
      )}
      sx={sx}
      size={size}
      clearOnBlur={false}
    />
  );
};

/** Shared autofill: installed-equipment item -> ticket defective fields. */
export const installedEquipmentYear = (item) => {
  const parsed = item?.installDate ? dayjs(item.installDate) : null;
  return parsed && parsed.isValid() ? parsed.year() : null;
};

/**
 * Shared form body for the two "create warranty ticket" surfaces. It is driven
 * entirely by the controller object returned by useWarrantyTicketForm. Each
 * surface supplies its own address section (search vs. fixed property) through
 * the `addressSlot` prop and chooses a compact (`dense`) or roomy layout.
 *
 * Section order matches the wizard steps: Scenario, Customer & PO, Issue,
 * Parts / Follow-up.
 */
export default function WarrantyTicketFormSections({ ctl, dense = false, isMobile = false, addressSlot = null }) {
  const {
    expertMode, currentStep,
    selectedProperty, ccProjectUrl,
    form, orderType, parts, suppliers, expandedRows, errors,
    showReview, setShowReview, emailTexts, setEmailTexts,
    softWarnings, softWarningsOpen, setSoftWarningsOpen,
    selectedPhotoIds, setSelectedPhotoIds,
    showSystemInfo, selectedSuppliers, partsHaveSuppliers,
    handleOrderTypeChange, handleFormChange, handleDefectiveModelChange,
    handlePartChange, handlePartItemTypeChange, handlePartSupplierChange, toggleRowExpanded, addPart, removePart,
    handleReviewOrder, handleSubmit,
  } = ctl;

  const fieldSize = dense ? 'small' : 'medium';
  const paperProps = dense
    ? { variant: 'outlined', sx: { p: 2, mb: 2 } }
    : { sx: { p: 3, mb: 2 } };
  const titleProps = dense
    ? { variant: 'subtitle1', sx: { mb: 1, fontWeight: 600 } }
    : { variant: 'h6', sx: { mb: 2, fontWeight: 600 } };

  const showScenario = expertMode || currentStep === 0;
  const showIssue = expertMode || currentStep === 2;
  const showParts = expertMode || currentStep === 3;

  return (
    <>
      {/* Step 0 — Scenario / order type */}
      {showScenario && (
        <>
          <WarrantyTicketOrderTypeSelector
            value={orderType}
            onChange={handleOrderTypeChange}
            sx={dense ? undefined : { p: 3 }}
          />
          {orderType === 'large' && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <strong>Large unit workflow — the order MUST go through this form.</strong>{' '}
              The supplier must receive an email with the ticket number,
              the warehouse must register the incoming unit, and the install crew
              needs a Service Proposal. Fill the defective model / serial in the next
              step, select a real supplier with email for each part, and use the
              "Review & Send Order" button before saving.
            </Alert>
          )}
          {orderType === 'small' && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Small part you will pick up yourself — phone-ordered supplier is OK.
              No warehouse entry, no install crew. Just record what you did so the
              ticket history is complete.
            </Alert>
          )}
          {orderType === 'on-site' && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Resolved on-site — only the issue description and what you did to fix it
              are required. After saving, mark the ticket as Resolved.
            </Alert>
          )}
          {orderType === 'follow-up' && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Follow-up visit — fill the date, time and reason in the Follow-up section
              so dispatcher can schedule it.
            </Alert>
          )}
        </>
      )}

      {/* Step 1 — Customer & PO (supplied by the host surface) */}
      {addressSlot}

      {/* Step 2 — System info + issue details */}
      {showIssue && (
        <>
          {showSystemInfo && (
            <Paper {...paperProps}>
              <Typography {...titleProps}>System Information</Typography>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DesktopDatePicker
                    label="Installation Year"
                    views={['year']}
                    inputFormat="YYYY"
                    value={form.installationYear ? dayjs(`${form.installationYear}-01-01`) : null}
                    maxDate={dayjs()}
                    onChange={(v) => handleFormChange('installationYear', v && v.isValid() ? v.year() : null)}
                    renderInput={(params) => <TextField {...params} fullWidth size={fieldSize} />}
                  />
                </LocalizationProvider>
                <TextField
                  label="Brand"
                  value={form.brandModel}
                  onChange={(e) => handleFormChange('brandModel', e.target.value.toUpperCase())}
                  inputProps={{ style: { textTransform: 'uppercase' } }}
                  fullWidth
                  size={fieldSize}
                />
              </Box>
            </Paper>
          )}

          <Paper {...paperProps}>
            <Typography {...titleProps}>Issue Details</Typography>
            <TextField
              label="Issue Description *"
              multiline
              rows={dense ? 3 : 4}
              value={form.issueDescription}
              onChange={(e) => handleFormChange('issueDescription', e.target.value)}
              error={!!errors.issueDescription}
              helperText={errors.issueDescription || `${form.issueDescription.length}/2000`}
              fullWidth
              size={fieldSize}
              sx={{ mb: dense ? 1.5 : 2 }}
            />

            <Box sx={{ mb: dense ? 1.5 : 2 }}>
              <Typography
                variant="subtitle2"
                sx={{ mb: 0.75, fontWeight: 600, color: 'text.secondary' }}
              >
                {orderType === 'large' ? 'Defective Equipment *' : 'Defective Equipment'}
              </Typography>
              <InstalledEquipmentPicker
                equipment={selectedProperty?.equipment}
                size={fieldSize}
                sx={{ mb: dense ? 1.5 : 2 }}
                onPick={(item) => {
                  handleDefectiveModelChange((item.model || '').toUpperCase());
                  handleFormChange('defectiveSerial', (item.serialNumber || '').toUpperCase());
                  const year = installedEquipmentYear(item);
                  if (year) handleFormChange('installationYear', year);
                }}
              />
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  label="Model"
                  value={form.defectiveModel}
                  onChange={(e) => handleDefectiveModelChange(e.target.value)}
                  placeholder="e.g. CARRIER 24ACC636A003"
                  fullWidth
                  size={fieldSize}
                  inputProps={{ style: { textTransform: 'uppercase' } }}
                  error={orderType === 'large' && !form.defectiveModel?.trim()}
                  helperText={
                    orderType === 'large' && !form.defectiveModel?.trim()
                      ? 'Required for warranty claim — take a photo of the label in CompanyCam if you forgot.'
                      : ' '
                  }
                />
                <TextField
                  label="Serial #"
                  value={form.defectiveSerial}
                  onChange={(e) => handleFormChange('defectiveSerial', e.target.value.toUpperCase())}
                  placeholder="e.g. 1234567890"
                  fullWidth
                  size={fieldSize}
                  inputProps={{ style: { textTransform: 'uppercase' } }}
                  error={orderType === 'large' && !form.defectiveSerial?.trim()}
                  helperText={
                    orderType === 'large' && !form.defectiveSerial?.trim()
                      ? 'Required for warranty claim — take a photo of the label in CompanyCam if you forgot.'
                      : ' '
                  }
                />
              </Box>
            </Box>

            <WarrantyTicketPhotoPicker
              propertyId={selectedProperty?.id}
              ccProjectUrl={ccProjectUrl}
              value={selectedPhotoIds}
              onChange={setSelectedPhotoIds}
              dense={dense}
            />

            <FormControl fullWidth size={fieldSize} sx={{ mb: dense ? 1.5 : 2 }}>
              <InputLabel>Priority *</InputLabel>
              <Select value={form.priority} label="Priority *" onChange={(e) => handleFormChange('priority', e.target.value)}>
                {PRIORITIES.map((p) => <MenuItem key={p} value={p}>{p}</MenuItem>)}
              </Select>
            </FormControl>

            <FormControl error={!!errors.workStatus} component="fieldset" sx={dense ? undefined : { mb: 1 }}>
              <FormLabel component="legend" sx={{ fontWeight: 600, ...(dense ? { fontSize: '0.875rem' } : {}) }}>Work Status *</FormLabel>
              <RadioGroup
                value={form.workStatus}
                onChange={(e) => handleFormChange('workStatus', e.target.value)}
                row={dense}
                sx={dense ? { flexWrap: 'wrap' } : undefined}
              >
                {WORK_STATUSES.map((ws) => (
                  <FormControlLabel
                    key={ws.value}
                    value={ws.value}
                    control={<Radio size={dense ? 'small' : 'medium'} />}
                    label={dense ? <Typography variant="body2">{ws.label}</Typography> : ws.label}
                  />
                ))}
              </RadioGroup>
              {errors.workStatus && <Typography variant="caption" color="error">{errors.workStatus}</Typography>}
            </FormControl>
          </Paper>
        </>
      )}

      {/* Step 3 — Parts / follow-up */}
      {showParts && (
        <>
          {PARTS_STATUSES.includes(form.workStatus) && (
            <Paper {...paperProps}>
              <Typography {...titleProps} sx={{ ...titleProps.sx, display: 'flex', alignItems: 'center', gap: 1 }}>
                <ShoppingCartIcon fontSize="small" />
                {form.workStatus === 'Parts received' ? 'Parts Received' : form.workStatus === 'Parts ordered' ? 'Parts Ordered' : 'Parts Needed — Order Form'}
              </Typography>

              {orderType === 'large' && parts.some((p) => p.supplierId === 'phone') && (
                <Alert severity="error" sx={{ mb: dense ? 1.5 : 2 }}>
                  <strong>Phone order is not allowed for a large unit.</strong>{' '}
                  Switch the supplier to a real entry with an email address so the
                  supplier receives the order and the warehouse knows what is incoming.
                  If the supplier is missing — ask the dispatcher to add it.
                </Alert>
              )}

              {form.workStatus === 'Parts received' && (
                <TextField
                  label="Parts Location"
                  value={form.partsLocation}
                  onChange={(e) => handleFormChange('partsLocation', e.target.value.toUpperCase())}
                  inputProps={{ style: { textTransform: 'uppercase' } }}
                  placeholder="e.g. WAREHOUSE A, SHELF 3"
                  fullWidth
                  size={fieldSize}
                  sx={{ mb: dense ? 1.5 : 2 }}
                />
              )}

              {isMobile ? (
                <Box sx={{ mb: dense ? 1.5 : 2 }}>
                  {parts.map((part, index) => (
                    <Paper key={index} variant="outlined" sx={{ p: 1.5, mb: 1, bgcolor: 'grey.50' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                        <PartCardBadge part={part} index={index} />
                        {parts.length > 1 && <IconButton size="small" onClick={() => removePart(index)} color="error"><DeleteIcon fontSize="small" /></IconButton>}
                      </Box>
                      <PartTypeToggle value={part.itemType} onChange={(v) => handlePartItemTypeChange(index, v)} sx={{ mb: 1 }} />
                      <TextField placeholder={partDescPlaceholder(part)} value={part.partDescription} onChange={(e) => handlePartChange(index, 'partDescription', e.target.value)}
                        error={!!errors[`part_${index}_desc`]} size="small" fullWidth sx={{ mb: 1, ...partDescSx(part) }} />
                      <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                        <TextField label="Qty" type="number" size="small" value={part.qty}
                          onChange={(e) => handlePartChange(index, 'qty', Math.max(1, parseInt(e.target.value) || 1))}
                          inputProps={{ min: 1 }} sx={{ width: 80 }} />
                        <FormControl fullWidth size="small">
                          <InputLabel>Supplier</InputLabel>
                          <Select value={part.supplierId || ''} label="Supplier" onChange={(e) => handlePartSupplierChange(index, e.target.value)}>
                            <MenuItem value=""><em>— Select —</em></MenuItem>
                            {suppliers.map((s) => <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>)}
                            <MenuItem value="phone"><em>Ordered by Phone</em></MenuItem>
                            <MenuItem value="other"><em>Other</em></MenuItem>
                          </Select>
                        </FormControl>
                      </Box>
                      {part.supplierId === 'other' && (
                        <TextField placeholder="Supplier name" value={part.orderedFrom} onChange={(e) => handlePartChange(index, 'orderedFrom', e.target.value)}
                          size="small" fullWidth sx={{ mb: 1 }} />
                      )}
                      <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                        <TextField label="PO Number" placeholder="Auto" value={part.poNumber}
                          InputProps={{ readOnly: true }} helperText="Auto-filled from ticket number"
                          size="small" sx={{ flex: 1 }} />
                        <TextField label="ETA" type="date" value={part.expectedArrival} onChange={(e) => handlePartChange(index, 'expectedArrival', e.target.value)}
                          size="small" InputLabelProps={{ shrink: true }} sx={{ flex: 1 }} />
                      </Box>
                      <TextField label="Special Instructions" placeholder="e.g., Need exact OEM part only..." fullWidth size="small"
                        value={part.instruction || ''} onChange={(e) => handlePartChange(index, 'instruction', e.target.value)} sx={{ mb: 1 }} />
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <TextField label="Failed Part #" placeholder="e.g. HC39GE237" value={part.failedPartNumber || ''}
                          onChange={(e) => handlePartChange(index, 'failedPartNumber', e.target.value)}
                          size="small" sx={{ flex: 1 }} />
                        <TextField label="Failed Part Serial" placeholder="Serial #" value={part.failedPartSerial || ''}
                          onChange={(e) => handlePartChange(index, 'failedPartSerial', e.target.value)}
                          size="small" sx={{ flex: 1 }} />
                      </Box>
                    </Paper>
                  ))}
                </Box>
              ) : (
                <TableContainer component={Paper} variant="outlined" sx={{ mb: dense ? 1.5 : 2 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: 'grey.100' }}>
                        <TableCell width="5%" align="center" />
                        <TableCell width="30%"><PartsHeaderLabel rows={parts} /></TableCell>
                        <TableCell width="10%" align="center"><strong>Qty</strong></TableCell>
                        <TableCell width="25%"><strong>Supplier</strong></TableCell>
                        <TableCell width="15%"><strong>PO Number</strong></TableCell>
                        <TableCell width="15%"><strong>ETA</strong></TableCell>
                        <TableCell width="5%" />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {parts.map((part, index) => (
                        <Fragment key={index}>
                          <TableRow hover>
                            <TableCell align="center" sx={{ p: 0.5 }}>
                              <IconButton size="small" onClick={() => toggleRowExpanded(index)} sx={{ color: (part.instruction || part.failedPartNumber || part.failedPartSerial) ? 'primary.main' : 'grey.400' }}>
                                {expandedRows[index] ? <KeyboardArrowUpIcon fontSize="small" /> : (part.instruction || part.failedPartNumber || part.failedPartSerial) ? <NotesIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
                              </IconButton>
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <PartTypeToggle value={part.itemType} onChange={(v) => handlePartItemTypeChange(index, v)} />
                                <TextField placeholder={partDescPlaceholder(part)} value={part.partDescription} onChange={(e) => handlePartChange(index, 'partDescription', e.target.value)}
                                  error={!!errors[`part_${index}_desc`]} size="small" fullWidth variant="standard" sx={partDescSx(part)} />
                              </Box>
                            </TableCell>
                            <TableCell align="center">
                              <TextField type="number" size="small" value={part.qty} onChange={(e) => handlePartChange(index, 'qty', Math.max(1, parseInt(e.target.value) || 1))}
                                inputProps={{ min: 1, style: { textAlign: 'center', width: 50 } }} variant="outlined" />
                            </TableCell>
                            <TableCell>
                              <FormControl fullWidth size="small">
                                <Select value={part.supplierId || ''} onChange={(e) => handlePartSupplierChange(index, e.target.value)} displayEmpty>
                                  <MenuItem value=""><em>— Select —</em></MenuItem>
                                  {suppliers.map((s) => <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>)}
                                  <MenuItem value="phone"><em>Ordered by Phone</em></MenuItem>
                                  <MenuItem value="other"><em>Other (manual)</em></MenuItem>
                                </Select>
                              </FormControl>
                              {part.supplierId === 'other' && (
                                <TextField placeholder="Supplier name" value={part.orderedFrom} onChange={(e) => handlePartChange(index, 'orderedFrom', e.target.value)}
                                  size="small" fullWidth variant="standard" sx={{ mt: 0.5 }} />
                              )}
                            </TableCell>
                            <TableCell>
                              <TextField placeholder="Auto" value={part.poNumber} InputProps={{ readOnly: true }} size="small" fullWidth variant="standard" />
                            </TableCell>
                            <TableCell>
                              <TextField type="date" value={part.expectedArrival} onChange={(e) => handlePartChange(index, 'expectedArrival', e.target.value)}
                                size="small" fullWidth InputLabelProps={{ shrink: true }} variant="standard" />
                            </TableCell>
                            <TableCell align="center" sx={{ p: 0.5 }}>
                              {parts.length > 1 && <IconButton size="small" onClick={() => removePart(index)} color="error"><DeleteIcon fontSize="small" /></IconButton>}
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell colSpan={7} sx={{ p: 0, borderBottom: expandedRows[index] ? undefined : 'none' }}>
                              <Collapse in={expandedRows[index]} timeout="auto" unmountOnExit>
                                <Box sx={{ p: 2, bgcolor: 'grey.50', display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                  <Box sx={{ display: 'flex', gap: 2 }}>
                                    <TextField label="Special Instructions" placeholder="e.g., Need exact OEM part only..." fullWidth size="small" multiline rows={2}
                                      value={part.instruction || ''} onChange={(e) => handlePartChange(index, 'instruction', e.target.value)}
                                      InputProps={{ startAdornment: <NoteAddIcon sx={{ mr: 1, color: 'grey.500' }} /> }} />
                                    <TextField label="Order Date" type="date" value={part.orderDate} onChange={(e) => handlePartChange(index, 'orderDate', e.target.value)}
                                      InputLabelProps={{ shrink: true }} size="small" sx={{ minWidth: 160 }} />
                                  </Box>
                                  <Box sx={{ display: 'flex', gap: 2 }}>
                                    <TextField label="Failed Part #" placeholder="Part # of the defective component, e.g. HC39GE237"
                                      value={part.failedPartNumber || ''} onChange={(e) => handlePartChange(index, 'failedPartNumber', e.target.value)}
                                      size="small" fullWidth />
                                    <TextField label="Failed Part Serial" placeholder="Serial # (if available)"
                                      value={part.failedPartSerial || ''} onChange={(e) => handlePartChange(index, 'failedPartSerial', e.target.value)}
                                      size="small" fullWidth />
                                  </Box>
                                </Box>
                              </Collapse>
                            </TableCell>
                          </TableRow>
                        </Fragment>
                      ))}
                      {parts.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={7} align="center">
                            <Typography color="text.secondary" sx={{ py: 2 }}>No parts added yet</Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <Button startIcon={<AddIcon />} onClick={addPart} variant="outlined" size="small">Add Part</Button>
                {partsHaveSuppliers && !showReview && (
                  <Button startIcon={<PreviewIcon />} onClick={handleReviewOrder} variant="contained" color="primary" size="small">Review & Send Order</Button>
                )}
              </Box>

              {showReview && (
                <Paper sx={{ p: 2, mt: 2, bgcolor: 'grey.50' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <EmailIcon color="primary" /> Review Email to Suppliers
                    </Typography>
                    <Button size="small" onClick={() => setShowReview(false)}>Back to Edit</Button>
                  </Box>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Review the email content below. You can edit the text for each supplier before sending. Emails will be sent when you click "Create Ticket &amp; Send Order".
                  </Alert>
                  {selectedSuppliers.map((supplier) => {
                    let supplierEmails = [];
                    if (supplier.id !== 'other' && supplier.id !== 'phone') {
                      supplierEmails = supplier.emails && Array.isArray(supplier.emails) ? supplier.emails : supplier.email ? [supplier.email] : [];
                    }
                    return (
                      <Paper key={supplier.id} variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'white' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{supplier.name}</Typography>
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            {supplierEmails.length > 0 ? supplierEmails.map((email, idx) => (
                              <Chip key={idx} label={email} size="small" color="primary" variant="outlined" />
                            )) : <Chip label="No email configured" size="small" color="warning" variant="outlined" />}
                          </Box>
                        </Box>
                        <TextField
                          fullWidth
                          multiline
                          rows={dense ? 8 : 10}
                          value={emailTexts[supplier.id] || ''}
                          onChange={(e) => setEmailTexts(prev => ({ ...prev, [supplier.id]: e.target.value }))}
                          variant="outlined"
                          sx={{ '& .MuiInputBase-root': { fontFamily: 'monospace', fontSize: dense ? '0.85rem' : '0.9rem', bgcolor: 'white' } }}
                        />
                      </Paper>
                    );
                  })}
                </Paper>
              )}
            </Paper>
          )}

          {(form.workStatus === 'Follow-up scheduled' || form.workStatus === 'Follow-up needed') && (
            <Paper {...paperProps}>
              <Typography {...titleProps}>Follow-up Details</Typography>
              <Box sx={{ display: 'flex', gap: 2, mb: dense ? 1.5 : 2 }}>
                <TextField label="Follow-up Date" type="date" value={form.followUpDate} onChange={(e) => handleFormChange('followUpDate', e.target.value)}
                  InputLabelProps={{ shrink: true }} size={fieldSize} sx={{ flex: 1 }} />
                <FormControl sx={{ flex: 1 }} size={fieldSize}>
                  <InputLabel>Time</InputLabel>
                  <Select value={form.followUpTime} label="Time" onChange={(e) => handleFormChange('followUpTime', e.target.value)}>
                    <MenuItem value="AM">AM</MenuItem>
                    <MenuItem value="PM">PM</MenuItem>
                    <MenuItem value="Full Day">Full Day</MenuItem>
                  </Select>
                </FormControl>
              </Box>
              <TextField label="Follow-up Reason" multiline rows={2} value={form.followUpReason}
                onChange={(e) => handleFormChange('followUpReason', e.target.value)} fullWidth size={fieldSize} />
            </Paper>
          )}
        </>
      )}

      {/* Soft-warning confirmation: shown before save when the chosen order */}
      {/* type indicates something is missing or inconsistent. The technician */}
      {/* can still proceed — we just want them to acknowledge. */}
      <Dialog open={softWarningsOpen} onClose={() => setSoftWarningsOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Before you save — please confirm</DialogTitle>
        <DialogContent dividers>
          {softWarnings.map((w, i) => (
            <Alert key={i} severity="warning" sx={{ mb: 1 }}>{w}</Alert>
          ))}
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            You can still save the ticket. Click "Go back and fix" if you want to correct
            these items first, or "Save anyway" to proceed.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSoftWarningsOpen(false)}>Go back and fix</Button>
          <Button variant="contained" color="warning" onClick={() => handleSubmit()}>Save anyway</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
